// src/components/AppFooter.tsx
import React from "react";
import { makeStyles } from "@fluentui/react-components";
import { Icon } from "@brand/Logo";
import { getBrandConfig } from "../brands/brand-registry";
import { APP_VERSION } from "../api/config";

const useStyles = makeStyles({
  footer: {
    display: "flex",
    height: "40px",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "18px", // WCAG 2.1 Level AA: Minimum 12px for readability
    color: "var(--footer-text-color, var(--text-primary))",
    backgroundColor: "var(--footer-bg-color, var(--bg-primary))",
    minHeight: "32px",
    maxWidth: "1080px",
    marginInline: "auto",
    width: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
    '@media (max-width: 600px)': {
      height: '32px',
      fontSize: '14px',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)', /* Safe area for touch assistance */
    },
  },
  left: {
    display: "flex",
    width: "25%",
    fontSize: "18px",
    justifyContent: "flex-start",
    gap: "8px",
    '@media (max-width: 600px)': {
      width: '25%',
      fontSize: '12px',
    },
  },
  right: {
    display: "flex",
    width: "25%",
    fontSize: "18px",
    marginRight: "0",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "8px",
    '@media (max-width: 600px)': {
      width: '25%',
      fontSize: '12px',
    },
  },
  footerLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "44px", // WCAG AAA: 44px minimum target size (2.5.5)
    minHeight: "44px", // WCAG AAA: 44px minimum target size (2.5.5)
    padding: "8px", // Increased padding for better touch target
    textDecoration: "none",
    gap: "4px", // Space between icon and text
    '@media (max-width: 600px)': {
      padding: '0px',
      gap: '0px',
    },
  },
  logo: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
    maxWidth: "20px",
    maxHeight: "20px",
    filter: "var(--footer-logo-filter, none)", // Brand-specific logo filter (e.g., white for ghost footer)
    '@media (max-width: 600px)': {
      width: '16px',
      height: '16px',
      maxWidth: '16px',
      maxHeight: '16px',
    },
  },
  linkText: {
    color: "var(--footer-text-color, var(--text-primary))",
    textDecoration: "underline",
    lineHeight: "1.5",
    '@media (max-width: 600px)': {
      fontSize: '12px',
    },
  },
  center: {
    display: "flex",
    fontSize: "18px",
    justifyContent: "center",
    textAlign: "center",
    gap: "8px",
    '@media (max-width: 600px)': {
      fontSize: "12px",
      padding: '0 4px',
    },
  },
});

export const AppFooter: React.FC = () => {
  const styles = useStyles();

  // Get brand configuration for dynamic content
  const brandConfig = getBrandConfig();
  
  // Extract footer config to avoid TypeScript narrowing issues with union types
  const footer = brandConfig.footer as {
    leftText: string;
    leftLink?: string;
    centerText: string;
    centerLink?: string;
    rightText: string;
    rightLink?: string;
  };
  const opensInNewTab = brandConfig.accessibility?.opensInNewTab ?? 'opens in new tab';
  const versionLabel = brandConfig.accessibility.versionLabel;

  return (
    <footer className={styles.footer}>
      {/* Left section: Text or hyperlink - controlled by brand config */}
      <div className={styles.left}>
        {footer.leftLink ? (
          <a
            href={footer.leftLink}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
            aria-label={`${footer.leftText} (${opensInNewTab})`}
          >
            <span className={styles.linkText}>
              {footer.leftText}
            </span>
          </a>
        ) : (
          <span>{footer.leftText}</span>
        )}
      </div>

      {/* Center section: Brand copyright text with link */}
      <div className={styles.center}>
        {footer.centerLink ? (
          <a
            href={footer.centerLink}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
            aria-label={`${footer.centerText} (${opensInNewTab})`}
          >
            <span className={styles.linkText}>
              {footer.centerText}
            </span>
          </a>
        ) : (
          <span>{footer.centerText}</span>
        )}
      </div>

      {/* Right section: Icon + title + app version - version always shown here */}
      <div className={styles.right}>
        {footer.rightLink ? (
          <a
            href={footer.rightLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${footer.rightText} ${versionLabel} ${APP_VERSION} (${opensInNewTab})`}
            title={`${footer.rightText} - ${versionLabel} (${APP_VERSION})`}
            className={styles.footerLink}
          >
            <Icon
              width={20}
              height={20}
              className={styles.logo}
              aria-hidden="true"
            />
            <span className={styles.linkText}>
              {footer.rightText}{APP_VERSION}
            </span>
          </a>
        ) : (
          <span className={styles.footerLink}>
            <Icon
              width={20}
              height={20}
              className={styles.logo}
              aria-hidden="true"
            />
            <span>
              {footer.rightText}{APP_VERSION}
            </span>
          </span>
        )}
      </div>
    </footer>
  );
};
